import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, Scan, FileText, RefreshCw, AlertTriangle, X, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

const BundleReceiptEntry = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const barcodeRef = useRef(null);
  const autoCloseAfterMismatchRef = useRef(false);
  
  // Inward Gate Entry verification state
  const [verificationMode, setVerificationMode] = useState(null); // 'usn_count' | 'usn_scanning' | null
  const [currentBundle, setCurrentBundle] = useState(null);
  const [usnScanInput, setUsnScanInput] = useState('');
  const [scannedUsns, setScannedUsns] = useState([]);
  const [missingUsns, setMissingUsns] = useState([]);
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);
  const usnInputRef = useRef(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Greige Storage', operationType: 'bundle_receipt', allowZero: true });

  // Scans persist server-side per scan, so the draft is a resumable bookmark of
  // the in-progress document + received count.
  useEffect(() => {
    if (!selectedDoc || !receiptData) return;
    const received = receiptData.received_bundles?.length || 0;
    autoSave({
      label: `${selectedDoc.document_barcode || selectedDoc.entry_id} — ${received} received`,
      bundleCount: received,
      documentId: selectedDoc.entry_id,
      documentNo: selectedDoc.document_barcode || selectedDoc.entry_id,
      payload: { doc: selectedDoc },
    });
  }, [receiptData, selectedDoc, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const d = await loadDraft(draftId);
      if (d.payload?.doc) await handleSelectDocument(d.payload.doc, true);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getDocumentsForReceipt();
      setDocuments(data.documents || []);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleSelectDocument = async (doc, fromDraft = false) => {
    if (!fromDraft) {
      const existing = (drafts || []).find((d) => d.document_id === doc.entry_id);
      if (existing) { await handleResumeDraft(existing.draft_id); return; }
      setResumedDraft(null);
    }
    try {
      setSelectedDoc(doc);
      if (!fromDraft) setActiveDraftId(null); // manual pick starts a fresh bookmark
      const receipt = await greigeStorageApi.startBundleReceipt(doc.entry_id);
      setReceiptData(receipt);
      setTimeout(() => barcodeRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start receipt');
    }
  };

  const handleScanBundle = async (e) => {
    // Fires from Enter key OR the Scan button click (click events have no `key`).
    if ((e && e.key && e.key !== 'Enter') || !bundleBarcode.trim()) return;
    
    try {
      setScanning(true);
      const result = await greigeStorageApi.scanBundleForReceipt(selectedDoc.entry_id, bundleBarcode.trim());
      
      // Check if this is from Inward Gate Entry and requires verification
      if (result.requires_verification && result.document_source === 'inward_gate_entry') {
        // Enter verification mode
        setCurrentBundle({
          barcode: bundleBarcode.trim(),
          usn_count: result.usn_count,
          usn_codes: result.usn_codes || []
        });
        setVerificationMode('usn_count');
        toast.info(`Bundle has ${result.usn_count} USNs. Please verify.`);
      } else {
        // Internal Transfer - auto approve and continue
        toast.success(`Bundle received (${result.bundles_received}/${result.bundles_received + result.bundles_pending})`);
        
        // Update local state
        setReceiptData(prev => ({
          ...prev,
          received_bundles: [...prev.received_bundles, { 
            bundle_barcode: bundleBarcode.trim(),
            usn_count: result.usn_count,
            usn_status: 'verified'
          }],
          pending_bundles: prev.pending_bundles.filter(b => b !== bundleBarcode.trim())
        }));

        // AUTO-CLOSE on the last bundle (no manual "Complete Receipt" step).
        if (result.auto_completed) {
          setBundleBarcode('');
          toast.success('All bundles received — receipt completed');
          resetReceipt();
          return;
        }
      }
      
      setBundleBarcode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan bundle');
      setBundleBarcode('');
    } finally {
      setScanning(false);
      if (!verificationMode) {
        // Defer refocus: the scan <Input> is disabled={scanning}, so a
        // synchronous focus() before React re-enables it is a no-op.
        setTimeout(() => barcodeRef.current?.focus(), 50);
      }
    }
  };

  const handleApproveBundle = async () => {
    try {
      const result = await greigeStorageApi.approveBundleUsnCount(selectedDoc.entry_id, currentBundle.barcode);
      toast.success('Bundle USN count approved');
      
      // Update local state
      setReceiptData(prev => ({
        ...prev,
        received_bundles: prev.received_bundles.map(b => 
          b.bundle_barcode === currentBundle.barcode 
            ? { ...b, usn_status: 'verified' }
            : b
        )
      }));
      
      // Reset verification state
      setVerificationMode(null);
      setCurrentBundle(null);
      setBundleBarcode('');

      // AUTO-CLOSE if this verified the last pending bundle.
      if (result.auto_completed) {
        toast.success('All bundles received — receipt completed');
        resetReceipt();
        return;
      }

      setTimeout(() => barcodeRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleQtyMismatch = async () => {
    try {
      const result = await greigeStorageApi.startQtyMismatch(selectedDoc.entry_id, currentBundle.barcode);
      toast.info(`Starting USN scan verification. Expected: ${result.expected_usn_count} USNs`);
      
      setVerificationMode('usn_scanning');
      setScannedUsns([]);
      setMissingUsns(result.expected_usns || currentBundle.usn_codes);
      setTimeout(() => usnInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start mismatch verification');
    }
  };

  const handleScanUsn = async (e) => {
    if (e.key !== 'Enter' || !usnScanInput.trim()) return;
    
    try {
      const result = await greigeStorageApi.scanUsnForMismatch(
        selectedDoc.entry_id, 
        currentBundle.barcode, 
        usnScanInput.trim()
      );
      
      setScannedUsns(prev => [...prev, usnScanInput.trim()]);
      setMissingUsns(result.missing_usns);
      toast.success(`USN scanned (${result.scanned_count}/${result.expected_count})`);
      setUsnScanInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'USN scan failed');
      setUsnScanInput('');
    }
  };

  const handleCompleteMismatch = async () => {
    try {
      const result = await greigeStorageApi.completeMismatchVerification(
        selectedDoc.entry_id,
        currentBundle.barcode
      );
      
      setMismatchDialogOpen(true);
      // If this mismatch resolved the last pending bundle, close the receipt
      // when the operator dismisses the mismatch dialog.
      autoCloseAfterMismatchRef.current = !!result.auto_completed;
      
      // Update local state
      setReceiptData(prev => ({
        ...prev,
        received_bundles: prev.received_bundles.map(b => 
          b.bundle_barcode === currentBundle.barcode 
            ? { ...b, usn_status: 'pending_correction', missing_count: result.missing_count }
            : b
        )
      }));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete mismatch');
    }
  };

  const closeMismatchDialog = () => {
    setMismatchDialogOpen(false);
    setVerificationMode(null);
    setCurrentBundle(null);
    setScannedUsns([]);
    setMissingUsns([]);
    setBundleBarcode('');
    if (autoCloseAfterMismatchRef.current) {
      autoCloseAfterMismatchRef.current = false;
      toast.success('All bundles received — receipt completed');
      resetReceipt();
      return;
    }
    setTimeout(() => barcodeRef.current?.focus(), 100);
  };

  const resetReceipt = () => {
    clearDraft();
    setSelectedDoc(null);
    setReceiptData(null);
    setResumedDraft(null);
    fetchDocuments();
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    resetReceipt();
  };

  const progress = receiptData 
    ? (receiptData.received_bundles?.length / receiptData.expected_bundles?.length) * 100 
    : 0;

  const isInwardGateEntry = receiptData?.document_source === 'inward_gate_entry';

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => {
                if (verificationMode) {
                  setVerificationMode(null);
                  setCurrentBundle(null);
                } else if (selectedDoc) {
                  setSelectedDoc(null);
                  setReceiptData(null);
                } else {
                  navigate('/sections/greige-storage');
                }
              }} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Bundle Receipt Entry</h1>
              <p className="text-sm text-[#E8DCC8]">
                {verificationMode === 'usn_scanning' ? 'USN Verification Mode' : 'Greige Storage'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            {receiptData && (
              <Badge className={isInwardGateEntry ? 'bg-amber-500' : 'bg-blue-500'}>
                {isInwardGateEntry ? 'Inward Gate Entry' : 'Internal Transfer'}
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {!selectedDoc ? (
          // Document Selection
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#1A1F2E] font-display">Select Document</h2>
                <p className="text-[#5A5F6E]">Choose a document to receive bundles</p>
              </div>
              <Button variant="outline" onClick={fetchDocuments} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : documents.length === 0 ? (
              <Card className="border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Documents Available</h3>
                  <p className="text-[#5A5F6E]">Accept incoming consignments first</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {documents.map((doc) => (
                  <Card 
                    key={doc.entry_id} 
                    className="border-[#E5DFD0] hover:border-[#2D5F4F] cursor-pointer transition-colors"
                    onClick={() => handleSelectDocument(doc)}
                    data-testid={`doc-${doc.entry_id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#1A1F2E]">{doc.entry_id}</p>
                            <p className="text-sm text-[#5A5F6E]">{doc.document_barcode || 'No document barcode'}</p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          {(drafts || []).some((d) => d.document_id === doc.entry_id) && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`doc-draft-${doc.entry_id}`}>
                              Draft
                            </Badge>
                          )}
                          <Badge className="bg-blue-100 text-blue-700 border-0">
                            {doc.bundles?.length || 0} Bundles
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : verificationMode === 'usn_count' ? (
          // USN Count Verification for Inward Gate Entry
          <Card className="border-2 border-amber-300 bg-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                USN Count Verification Required
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white p-4 rounded-lg border border-amber-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Bundle Barcode</p>
                    <p className="font-semibold text-lg">{currentBundle?.barcode}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">USN Count</p>
                    <p className="font-bold text-2xl text-amber-700">{currentBundle?.usn_count}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-amber-800 text-center">
                This bundle has <strong>{currentBundle?.usn_count}</strong> USNs attached.
                Please verify the physical count matches.
              </p>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={handleApproveBundle}
                  className="bg-green-600 hover:bg-green-700 px-8"
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  onClick={handleQtyMismatch}
                  variant="destructive"
                  className="px-8"
                  data-testid="qty-mismatch-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Qty Mismatch
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : verificationMode === 'usn_scanning' ? (
          // USN Scanning Mode
          <div className="space-y-4">
            <Card className="border-2 border-red-300 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <Scan className="w-5 h-5" />
                  USN Scanning Mode - {currentBundle?.barcode}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Expected</p>
                    <p className="text-xl font-bold">{currentBundle?.usn_count}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Scanned</p>
                    <p className="text-xl font-bold text-green-600">{scannedUsns.length}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Missing</p>
                    <p className="text-xl font-bold text-red-600">{missingUsns.length}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Scan USN Barcode</label>
                  <Input
                    ref={usnInputRef}
                    value={usnScanInput}
                    onChange={(e) => setUsnScanInput(e.target.value)}
                    onKeyDown={handleScanUsn}
                    placeholder="Scan USN barcode..."
                    className="mt-1"
                    data-testid="usn-scan-input"
                  />
                </div>
                
                <Button 
                  onClick={handleCompleteMismatch}
                  className="w-full bg-red-600 hover:bg-red-700"
                  data-testid="complete-mismatch-btn"
                >
                  Complete & Report Mismatch ({missingUsns.length} missing)
                </Button>
              </CardContent>
            </Card>
            
            {/* Scanned USNs */}
            {scannedUsns.length > 0 && (
              <Card className="border-green-200">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm text-green-700">Scanned USNs ({scannedUsns.length})</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="flex flex-wrap gap-2">
                    {scannedUsns.map(usn => (
                      <Badge key={usn} className="bg-green-100 text-green-700 border-0 text-xs">
                        {usn}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Missing USNs */}
            {missingUsns.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm text-red-700">Missing USNs ({missingUsns.length})</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="flex flex-wrap gap-2">
                    {missingUsns.slice(0, 30).map(usn => (
                      <Badge key={usn} className="bg-red-100 text-red-700 border-0 text-xs">
                        {usn}
                      </Badge>
                    ))}
                    {missingUsns.length > 30 && (
                      <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">
                        +{missingUsns.length - 30} more
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          // Bundle Scanning (main flow)
          <>
            {resumedDraft && (
              <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} className="mb-4" />
            )}
            <Card className="border-[#E5DFD0] mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Document: {selectedDoc.entry_id}</span>
                  <div className="flex items-center gap-2">
                    {receiptData?.origin_place && (
                      <Badge variant="outline" className="text-xs">
                        From: {receiptData.origin_place}
                      </Badge>
                    )}
                    <Badge className="bg-green-100 text-green-700 border-0">
                      {receiptData?.received_bundles?.length || 0} / {receiptData?.expected_bundles?.length || 0}
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Progress value={progress} className="h-2 mb-4" />
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#1A1F2E]">Scan Bundle Barcode</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        ref={barcodeRef}
                        value={bundleBarcode}
                        onChange={(e) => setBundleBarcode(e.target.value)}
                        onKeyDown={handleScanBundle}
                        placeholder="Scan or enter bundle barcode..."
                        disabled={scanning}
                        className="flex-1"
                        data-testid="bundle-barcode-input"
                      />
                      <Button onClick={handleScanBundle} disabled={!bundleBarcode.trim() || scanning} data-testid="gs-scan-bundle-btn">
                        <Scan className="w-4 h-4" />
                      </Button>
                    </div>
                    {isInwardGateEntry && (
                      <p className="text-xs text-amber-600 mt-1">
                        Inward Gate Entry: USN count verification required for each bundle
                      </p>
                    )}
                  </div>

                  {receiptData?.pending_bundles?.length === 0 && (
                    <div
                      className="w-full rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-center text-sm font-medium text-green-700 flex items-center justify-center gap-2"
                      data-testid="receipt-complete-note"
                    >
                      <Check className="w-4 h-4" />
                      All bundles received — this receipt closes automatically.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pending Bundles */}
            {receiptData?.pending_bundles?.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader>
                  <CardTitle className="text-base">Pending Bundles ({receiptData.pending_bundles.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {receiptData.pending_bundles.slice(0, 20).map((barcode) => (
                      <Badge key={barcode} variant="outline" className="text-xs">
                        {barcode}
                      </Badge>
                    ))}
                    {receiptData.pending_bundles.length > 20 && (
                      <Badge variant="outline" className="text-xs bg-gray-100">
                        +{receiptData.pending_bundles.length - 20} more
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Received Bundles */}
            {receiptData?.received_bundles?.length > 0 && (
              <Card className="border-[#E5DFD0] mt-4">
                <CardHeader>
                  <CardTitle className="text-base text-green-700">
                    <Check className="w-4 h-4 inline mr-2" />
                    Received ({receiptData.received_bundles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {receiptData.received_bundles.slice(-20).map((b) => (
                      <Badge 
                        key={b.bundle_barcode} 
                        className={`border-0 text-xs ${
                          b.usn_status === 'pending_correction' 
                            ? 'bg-red-100 text-red-700' 
                            : b.usn_status === 'pending_verification'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {b.bundle_barcode}
                        {b.usn_count > 0 && ` (${b.usn_count} USNs)`}
                        {b.usn_status === 'pending_correction' && ' ⚠️'}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Mismatch Report Dialog */}
      <Dialog open={mismatchDialogOpen} onOpenChange={setMismatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Mismatch Reported
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p>
              <strong>{missingUsns.length}</strong> USNs are missing from bundle{' '}
              <strong>{currentBundle?.barcode}</strong>
            </p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 mb-2">Missing USNs have been reported to:</p>
              <ul className="text-sm text-red-600 list-disc ml-4">
                <li>Origin: {receiptData?.origin_place}</li>
                <li>Accounts Module</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600">
              Bundle status set to <strong>Pending Correction</strong>.
              It will remain pending until a corrected document is received from the origin.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={closeMismatchDialog}>
              Continue Scanning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BundleReceiptEntry;
